import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import animationData from './AnimatedArrow.json';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

const Lottie = dynamic(
  () => import('@lottiefiles/react-lottie-player').then(mod => mod.Player),
  { ssr: false }
);

export const HeroDetails = ({
  animate,
  propertiesRef
}: {
  animate: boolean;
  propertiesRef: React.RefObject<HTMLDivElement>;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { t } = useTranslation('nawy-now');
  const { locale } = useRouter();

  const handleClick = () => {
    if (!propertiesRef.current) return;
    const offset = isMobile ? 70 : 100;
    const elementPosition = propertiesRef?.current?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    if (animate && containerRef.current) {
      gsap.fromTo(
        containerRef.current,
        {
          opacity: 0,
          left: isMobile ? 'unset' : locale === 'ar' ? 'unset' : -300,
          right: isMobile ? 'unset' : locale === 'ar' ? -300 : 'unset',
          x: isMobile ? (locale === 'ar' ? 200 : -200) : 'unset',
          y: isMobile ? 20 : 45
        },
        {
          opacity: 1,
          x: isMobile ? (locale === 'ar' ? -20 : 20) : 'unset',
          y: isMobile ? 20 : 45,
          left: !isMobile && locale !== 'ar' ? 'calc(50% - 600px)' : 'unset',
          right: !isMobile && locale === 'ar' ? 'calc(50% - 600px)' : 'unset',
          duration: 1,
          ease: 'power2.inOut',
          display: 'flex',
          pointerEvents: 'auto'
        }
      );
    } else {
      gsap.to(containerRef.current, {
        opacity: 0,
        x: isMobile
          ? locale === 'ar'
            ? 200
            : -200
          : locale === 'ar'
            ? 300
            : -300,
        display: 'none',
        pointerEvents: 'none',
        duration: 1,
        ease: 'power2.inOut'
      });
    }
  }, [animate, locale, isMobile]);

  return (
    <Container ref={containerRef}>
      <div className="typo">
        <span className="hero-title">{t('hero_title')}</span>
        <span className="hero-description">{t('hero_description')}</span>
      </div>
      <button className="hero-action" onClick={handleClick}>
        <span>{t('go_to_properties')}</span>
        <Lottie
          autoplay
          loop
          src={animationData}
          style={{
            height: isMobile ? '28px' : '38px',
            width: isMobile ? '28px' : '38px'
          }}
        />
      </button>
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  opacity: 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
  z-index: 999;
  .typo {
    display: flex;
    flex-direction: column;
    gap: 16px;
    h2 {
      color: #2c3138;
      font-family: materialBold;
      font-size: 35px;
      line-height: 46px;
      text-transform: uppercase;
    }
    .hero-description {
      color: #2c3138;
      font-family: materialRegular;
      font-size: 16px;
      line-height: normal;
      text-transform: capitalize;
      max-width: 320px;
    }
    .hero-title {
      color: #2c3138;
      font-family: materialBold;
      font-size: 35px;
      line-height: 46px;
      text-transform: uppercase;
    }
  }

  .hero-action {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #ff5e00;
    border-radius: 12px;
    color: #fff;
    cursor: pointer;
    border: none;
    outline: none;
    width: fit-content;
    span {
      color: #fff;
      text-align: center;
      font-family: materialMedium;
      font-size: 16px;
      line-height: normal;
      text-transform: capitalize;
    }
  }

  @media screen and (max-width: 768px) {
    gap: 12px;
    .typo {
      gap: 4px;

      h2 {
        font-size: 18px;
        line-height: 23px;
      }
      .hero-description {
        font-size: 12px;
        max-width: 175px;
        line-height: 18px;
      }
    }

    .hero-action {
      border-radius: 8px;
      gap: 0;
      padding: 0 8px;
      padding-inline-end: 0;
      span {
        font-size: 12px;
      }
    }
  }
`;

export default HeroDetails;
